import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
// import type { CareStageCardData } from './understandCareStageData';
import type { CareStageCardData } from './understandCareStageData';
import { apiClient } from '@/core/api/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';

type CardsApiResponse = {
  overviewNarrative: string | null;
  cards: CareStageCardData[];
};

type UnderstandCareStageProps = { careStageId?: string | null };

const getStoredCareStageId = (): string | null => {
  return sessionStorage.getItem('bquest_care_stage_id');
};

export function UnderstandCareStage({ careStageId }: UnderstandCareStageProps) {
  const resolvedCareStageId = careStageId ?? getStoredCareStageId();
  const [selectedCard, setSelectedCard] = useState<CareStageCardData | null>(null);
  const { data } = useQuery<CardsApiResponse>({
    queryKey: ['care-stage-consideration-cards', resolvedCareStageId],
    enabled: !!resolvedCareStageId,
    queryFn: async () => {
      const { data } = await apiClient.get<CardsApiResponse>(
        '/resources/care-stage-consideration-cards',
        { params: { care_stage_id: resolvedCareStageId } }
      );
      return data;
    },
  });

  const careStageCards = data?.cards ?? [];
  const overviewNarrative = data?.overviewNarrative ?? null;

  if (!careStageCards.length) {
    return null;
  }

  return (
    <section className="mx-auto max-w-6xl py-8">
      <div className="mx-auto max-w-5xl text-center">
        <h2 className="text-3xl font-semibold text-gray-900 sm:text-4xl">
          Understand Your Care Stage
        </h2>
        <p className="mt-8 text-xl text-gray-600">
          Explore key areas of your care planning journey.
        </p>
        {overviewNarrative ? (
          <p className="mt-14 text-lg leading-8 text-gray-800">{overviewNarrative}</p>
        ) : null}
        {/* <p className="mt-14 text-lg leading-8 text-gray-800">
          Aging at home doesn&apos;t happen by accident&mdash;it happens with a plan.
        </p>
        <p className="mt-6 text-lg leading-8 text-gray-800">
          The earlier you think through key decisions, the more control you have over how and where
          care happens. Below are the core areas to consider when planning to age in place. You
          don&apos;t need to solve everything at once&mdash;but understanding what to think about
          (and when) can help you avoid stress, rushed decisions, and unnecessary costs later.
        </p> */}
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {careStageCards.map((card) => (
          <button
            key={card.id}
            type="button"
            onClick={() => setSelectedCard(card)}
            className="rounded-2xl border border-gray-200 bg-white p-8 text-left transition-all duration-200 hover:border-green-500 hover:shadow-md"
          >
            <h4 className=" font-semibold  leading-snug">{card.title}</h4>

            <p className="mt-5 text-gray-500  leading-8">{card.description}</p>
          </button>
        ))}
      </div>

      <Dialog open={!!selectedCard} onOpenChange={(isOpen) => !isOpen && setSelectedCard(null)}>
        {selectedCard && (
          <DialogContent className="flex max-h-[80vh] max-w-2xl flex-col p-0 sm:rounded-lg">
            <div className="flex-1 overflow-y-auto px-8 pt-7 pb-8">
              <DialogHeader className="mb-4">
                <DialogTitle className="pr-8 text-2xl font-bold leading-snug text-bquest-green">
                  {selectedCard.title}
                </DialogTitle>
              </DialogHeader>
              <DialogDescription asChild>
                <div className="space-y-5 text-left">
                  <p className="text-sm leading-6 text-gray-700">{selectedCard.popup.body}</p>

                  <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="text-sm font-semibold text-gray-900">Why this matters:</h4>
                    <p className="mt-2 text-sm leading-6 text-gray-600">
                      {selectedCard.popup.whyItMatters}
                    </p>
                  </section>

                  {selectedCard.popup.considerations.length > 0 && (
                    <section>
                      <h4 className="text-sm font-semibold text-gray-900">
                        As you think about this, consider:
                      </h4>
                      <ul className="mt-2 space-y-1.5 text-sm leading-6 text-gray-700">
                        {selectedCard.popup.considerations.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span className="mt-0.5 shrink-0 text-gray-400">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {selectedCard.popup.professionals.length > 0 && (
                    <section className="rounded-lg border border-gray-200 bg-white p-4">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Professionals who support this:
                      </h4>
                      <ul className="mt-2 space-y-1.5 text-sm leading-6">
                        {selectedCard.popup.professionals.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span className="mt-0.5 shrink-0 text-gray-900">•</span>
                            <span className="text-gray-900">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </div>
              </DialogDescription>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </section>
  );
}
